import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, TextField, Button, Typography, CircularProgress, MenuItem, Select, InputLabel, FormControl, Card } from "@mui/material";
import { doc, addDoc, getDoc, getDocs, collection, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "./firebaseServices";
import SnackbarMessage from "./SnackbarMessage";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

function AddEditProduct() {
  const [tabValue, setTabValue] = useState(0); // Track active tab

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // State for adding products
  const [productName, setProductName] = useState("");
  const [sku, setSku] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  // State for editing products
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");

  useEffect(() => {
    // Fetch categories and products when the component mounts
    const fetchData = async () => {
      try {
        // Fetch categories
        const categorySnapshot = await getDocs(collection(db, "categories"));
        const categoryList = categorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoryList);

        // Fetch products
        const productSnapshot = await getDocs(collection(db, "products"));
        const productList = productSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // ✅ Sort alphabetically by SKU or Product Name
        productList.sort((a, b) => a.sku.localeCompare(b.sku)); // Sort by SKU
        setProducts(productList);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    clearForm();
  };

  const clearForm = () => {
    setProductName("");
    setSku("");
    setDescription("");
    setCategory("");
    setSelectedProduct("");
    setMessage("");
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!productName || !sku || !description || !category) {
      setMessage("Please fill in all fields.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const newProduct = {
        productName,
        sku,
        description,
        category,
      };

      // 🔹 Add document with an auto-generated ID
      const docRef = await addDoc(collection(db, "products"), newProduct);

      // Update state to reflect the new product
      setProducts((prevProducts) => [...prevProducts, { id: docRef.id, ...newProduct }]);

      setMessage(`Product "${productName}" added successfully!`);
      setTimeout(() => {
        clearForm();
      }, 3000);
    } catch (error) {
      console.error("Error adding product:", error);
      setMessage("Failed to add product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = async (e) => {
    const selectedId = e.target.value;
    setSelectedProduct(selectedId);

    if (selectedId) {
      try {
        const productDoc = await getDoc(doc(db, "products", selectedId));
        if (productDoc.exists()) {
          const data = productDoc.data();
          setProductName(data.productName);
          setSku(data.sku);
          setDescription(data.description);
          setCategory(data.category);
        }
      } catch (error) {
        console.error("Error loading product data:", error);
      }
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();

    if (!selectedProduct) {
      setMessage("Please select a product to edit.");
      return;
    }

    if (!productName || !sku || !description || !category) {
      setMessage("Please fill in all fields.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const productDoc = doc(db, "products", selectedProduct); // selectedProduct should be the document ID

      // Update only the fields provided in the form
      await updateDoc(productDoc, {
        productName: productName || null,
        sku: sku || null,
        description: description || null,
        category: category || null,
      });

      setMessage(`Product "${productName}" updated successfully!`);
      setTimeout(() => {
        clearForm();
      }, 3000);
    } catch (error) {
      console.error("Error updating product:", error);
      setMessage("Failed to update product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) {
      setMessage("Please select a product to delete.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const productDoc = doc(db, "products", selectedProduct);
      await deleteDoc(productDoc);

      // Remove the product from state after deletion
      setProducts((prevProducts) => prevProducts.filter((prod) => prod.id !== selectedProduct));

      setMessage("Product deleted successfully!");
      setOpenDeleteDialog(false); // Close the dialog
      clearForm();
    } catch (error) {
      console.error("Error deleting product:", error);
      setMessage("Failed to delete product. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null); // Reset selected product when dialog is closed
  };

  const handleCardClick = (product) => {
    console.log("Product clicked:", product);
    console.log(openDialog);
    setSelectedProduct(product);
    setOpenDialog(true); // Open the dialog on card click
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Card
        sx={{
          backgroundColor: "white",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          padding: 3,
          maxWidth: "800px",
          margin: "auto",
          mt: 4,
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Add Product" />
          <Tab label="Edit Products" />
        </Tabs>
        {tabValue === 0 && (
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            {/* Left Side: Add Product Form */}
            <Box component="form" onSubmit={handleAddProduct} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <TextField
                label="Product Name"
                variant="outlined"
                fullWidth
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField label="SKU" variant="outlined" fullWidth value={sku} onChange={(e) => setSku(e.target.value)} required sx={{ mb: 2 }} />
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Category</InputLabel>
                <Select value={category} onChange={(e) => setCategory(e.target.value)} required>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.catName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
                {loading ? <CircularProgress size={24} /> : "Add Product"}
              </Button>
              <SnackbarMessage message={message} setMessage={setMessage} /> {/* Include Snackbar */}
            </Box>

            {/* Right Side: Scrolling List of SKUs */}
            <Box
              sx={{
                flex: 1,
                maxHeight: "400px",
                overflowY: "auto",
                border: "1px solid #ccc",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Existing Product SKUs
              </Typography>
              {products.length ? (
                products.map((prod) => (
                  <Button key={prod.id} variant="outlined" fullWidth onClick={() => handleCardClick(prod)}>
                    {prod.sku}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No products available.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {tabValue === 1 && (
          <Box component="form" onSubmit={handleEditProduct} sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Product</InputLabel>
              <Select value={selectedProduct} onChange={handleProductSelect} required>
                {products.map((prod) => (
                  <MenuItem key={prod.id} value={prod.id}>
                    <Typography sx={{ fontWeight: "bold" }}>{prod.sku} </Typography> - {prod.productName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Product Name"
              variant="outlined"
              fullWidth
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField label="SKU" variant="outlined" fullWidth value={sku} onChange={(e) => setSku(e.target.value)} required sx={{ mb: 2 }} />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Category</InputLabel>
              <Select value={category} onChange={(e) => setCategory(e.target.value)} required>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.catName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Update Product"}
            </Button>
            {/* Delete Button */}
            <Button
              variant="contained"
              color="error"
              fullWidth
              sx={{ mt: 2 }}
              disabled={!selectedProduct || loading}
              onClick={() => setOpenDeleteDialog(true)} // Open confirmation dialog
            >
              Delete Product
            </Button>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
              <DialogTitle>Are you sure?</DialogTitle>
              <DialogContent>
                <Typography>Do you really want to delete this product? This action cannot be undone.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
                  Cancel
                </Button>
                <Button onClick={handleDeleteProduct} color="error">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
        <SnackbarMessage message={message} setMessage={setMessage} /> {/* Include Snackbar */}
      </Card>
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth={true} maxWidth="lg">
        {/* 🔴 Red Bar at the Top */}
        <Box sx={{ backgroundColor: "red", color: "white", padding: 2, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Viewing Details for {selectedProduct?.productName}
          </Typography>
        </Box>

        <DialogContent>
          <Typography variant="h6">{selectedProduct?.productName}</Typography>

          <Typography variant="body1">SKU: {selectedProduct?.sku}</Typography>

          <Typography variant="h6" sx={{ marginTop: 1 }}>
            Description:{" "}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {selectedProduct?.description}
          </Typography>

          {selectedProduct?.images && selectedProduct.images.length > 0 ? (
            <Box sx={{ marginTop: 2 }}>
              {selectedProduct.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "inline-block",
                    textAlign: "center",
                    marginRight: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <img src={image} alt={`${selectedProduct.productName} ${index + 1}`} style={{ width: "200px", height: "auto" }} />
                  <Box></Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No images available
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddEditProduct;
